<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import type { Editor } from "@tiptap/vue-3";
import type { Option } from "@/Components/UI/TcSelect.vue";

import { TcModal, TcButton, TcSelect, TcInput } from "@/Components/UI";

import { hasValue } from "@/Utils/Objects";
import { isValidString } from "@/Utils/String";
import { unixUTC } from "@/Utils/Date";

import { CUSTOM_INPUT_OPTIONS, FREE_CUSTOM_INPUT_VALUE } from "./assets";
import { getRandomId } from "./extensions/utils";

export interface CustomActionsProps {
  editor: Editor;
}

const props = defineProps<CustomActionsProps>();

const { t } = useI18n();

const customInput = ref<Option<string> | undefined>(undefined);
const customLabel = ref<string>("");

const inputOptions = computed(() =>
  CUSTOM_INPUT_OPTIONS.map(({ label, value }) => ({ label: t(label), value }))
);

const showCustomInputModal = () => {
  customInput.value = CUSTOM_INPUT_OPTIONS[0]!;
};

const hideCustomInputModal = () => {
  customInput.value = undefined;
};

const handleAddCustomInput = () => {
  let placeholder = customInput.value!.label;
  let name = customInput.value!.value;

  if (customInput.value!.value === FREE_CUSTOM_INPUT_VALUE) {
    if (!isValidString(customLabel.value)) {
      return;
    }

    placeholder = customLabel.value;
    name = `${FREE_CUSTOM_INPUT_VALUE}_${unixUTC()}`;
  }

  props.editor.chain().focus().insertCustomInput({ placeholder, name }).run();

  customInput.value = undefined;
};

const handleAddSign = () => {
  props.editor.chain().focus().insertSign().run();
};

const handleAddCustomCheckbox = () => {
  props.editor
    .chain()
    .focus()
    .insertCustomCheckbox({ name: `checkbox_${getRandomId()}` })
    .run();
};
</script>

<template>
  <div class="custom_actions">
    <TcButton
      variant="outlined"
      prefixIcon="Plus"
      prefixIconColor="primary-high"
      isFitContent
      isSmall
      :isBlock="false"
      @click="showCustomInputModal"
    >
      {{ t("documents.editor.actions.add.custom_input") }}
    </TcButton>

    <TcButton
      variant="outlined"
      prefixIcon="Plus"
      prefixIconColor="primary-high"
      isFitContent
      isSmall
      :isBlock="false"
      @click="handleAddSign"
    >
      {{ t("documents.editor.actions.add.sign") }}
    </TcButton>

    <TcButton
      variant="outlined"
      prefixIcon="Plus"
      prefixIconColor="primary-high"
      isFitContent
      isSmall
      :isBlock="false"
      @click="handleAddCustomCheckbox"
    >
      {{ t("documents.editor.actions.add.check") }}
    </TcButton>

    <TcModal
      v-if="hasValue(customInput)"
      :title="t('documents.editor.actions.add_input_modal.title')"
      :acceptButtonText="t('documents.editor.actions.add_input_modal.actions.add')"
      canClose
      @acceptButtonClicked="handleAddCustomInput"
      @clickOutside="hideCustomInputModal"
    >
      <template #description>
        <TcSelect name="html_editr_custom_input" v-model="customInput" :options="inputOptions" />

        <TcInput
          v-if="customInput!.value === '---'"
          v-model="customLabel"
          :label="t('documents.editor.actions.add_input_modal.free.label')"
        />
      </template>
    </TcModal>
  </div>
</template>

<style scoped lang="scss">
.custom_actions {
  @include sp.padding(y, 5);

  display: flex;
  gap: $space-4;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: $breakpoint-m) {
    flex-direction: column;
    gap: $space-3;

    & > * {
      width: 100%;
    }
  }
}
</style>
